/**
 * SixWaySmoke — six-way lit smoke / dust for the GPU particle billboards
 * (shader additions wave, 2026-09-15; the Unity VFX Graph "six-way lighting"
 * representation).
 *
 * A simulated puff's response to light from six axis directions is baked
 * offline into two RGBA flipbooks (`tools/asset-pipeline/gen-sixway-smoke.mjs`
 * in Atlas bakes a procedural one); at runtime the billboard reconstructs
 * directional lighting from them and keeps a sprite's cost. Packing (Unity's):
 *
 *   map A :  R = lit from +X (right)   G = +Y (top)     B = −Z (BACK)   A = coverage
 *   map B :  R = lit from −X (left)    G = −Y (bottom)  B = +Z (FRONT)  A = emissive
 *
 * The maps store directional light RESPONSE (0..1), not albedo. Axes are the
 * displayed quad's own: +X = the quad's right, +Y = its up, +Z = TOWARD THE
 * CAMERA — so "front" is lit when the light sits on the camera's side and
 * "back" when it is behind the puff. The light is brought into that basis in
 * the vertex stage (view space already IS the billboard basis; the stretched
 * mode rotates x/y by its screen axes), and the fragment reconstructs with
 * squared positive axis weights, which sum to one for a unit direction:
 *
 *   S(ℓ) = Σ_a S_a · max(0, a·ℓ)²      a ∈ {±x, ±y, ±z}
 *
 * The response is NOT multiplied by a Lambert N·L — the bake already holds the
 * volume's directional behaviour. Ambient uses the mean of the six. Emission is
 * a separate channel so a hot core can fade on its own clock. This does not
 * make the sprite a volume: no particle-to-particle shadowing.
 */
import type * as THREE from 'three/webgpu';
import { vec3, float } from 'three/tsl';

export interface SixWayMaps {
  mapA: THREE.Texture;
  mapB: THREE.Texture;
  /** Flipbook grid. */
  columns: number;
  rows: number;
  /** Frames per second; the flipbook spans the particle's life when 0 (default). */
  fps?: number;
}

export interface SixWayWeights { px: number; nx: number; py: number; ny: number; pz: number; nz: number }

/** Squared positive axis weights for a light direction in the billboard basis. */
export function sixWayWeightsCpu(lx: number, ly: number, lz: number): SixWayWeights {
  const p = (v: number): number => (v > 0 ? v * v : 0);
  const n = (v: number): number => (v < 0 ? v * v : 0);
  return { px: p(lx), nx: n(lx), py: p(ly), ny: n(ly), pz: p(lz), nz: n(lz) };
}

/**
 * Reconstructed lit response for baked responses `a` = [right, top, back] and
 * `b` = [left, bottom, front] and a light direction in the billboard basis.
 */
export function sixWayResponseCpu(a: [number, number, number], b: [number, number, number], lx: number, ly: number, lz: number): number {
  const w = sixWayWeightsCpu(lx, ly, lz);
  return a[0] * w.px + b[0] * w.nx + a[1] * w.py + b[1] * w.ny + a[2] * w.nz + b[2] * w.pz;
}

/** Light direction (world, toward the light) into the billboard basis given the quad's world axes. */
export function billboardLightBasisCpu(
  right: [number, number, number], up: [number, number, number], toCamera: [number, number, number],
  light: [number, number, number],
): [number, number, number] {
  const d = (a: [number, number, number]): number => a[0] * light[0] + a[1] * light[1] + a[2] * light[2];
  return [d(right), d(up), d(toCamera)];
}

/**
 * TSL: reconstruct from two sampled vec4 nodes and a billboard-basis light
 * direction node. Returns nodes for the directional response, the ambient
 * (mean) response, coverage and emission.
 */
export function sixWayLitNodes(a: any, b: any, lLocal: any): { response: any; ambient: any; alpha: any; emissive: any } {
  const pos = vec3(lLocal).max(0), neg = vec3(lLocal).negate().max(0);
  const wp = pos.mul(pos), wn = neg.mul(neg);
  const response = a.x.mul(wp.x).add(b.x.mul(wn.x))
    .add(a.y.mul(wp.y)).add(b.y.mul(wn.y))
    .add(a.z.mul(wn.z)).add(b.z.mul(wp.z));
  const ambient = a.x.add(a.y).add(a.z).add(b.x).add(b.y).add(b.z).div(float(6));
  return { response, ambient, alpha: a.w, emissive: b.w };
}
